/**
 * BottomNav — orchestrator component.
 * Renders the BottomDock + all four drawers.
 * Manages open/close state, fetches data via useBottomNavData,
 * and applies role-based conditional rendering.
 *
 * Only renders on mobile (lg:hidden is enforced inside BottomDock).
 * Mount this once inside MainLayout, outside <main>, above the footer.
 *
 * Notification system:
 *  - For logged-in players with a claimed+approved profile, notifications are
 *    fetched from `player_notifications` via usePlayerNotifications.
 *  - For admins, the existing role-based notification logic in useBottomNavData
 *    is preserved (pending adjustments, pending registrations).
 *  - The two systems are merged: player notifications take priority, admin
 *    notifications are appended.
 */
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useSeason } from "../../context/SeasonContext";
import { usePendingAdjustments } from "../../hooks/usePendingAdjustments";
import { usePendingRegistrations } from "../../hooks/usePendingRegistrations";
import { useBottomNavData } from "../../hooks/useBottomNavData";
import { usePlayerNotifications } from "../../hooks/usePlayerNotifications";

import BottomDock from "./BottomDock";
import TeamsDrawer from "./TeamsDrawer";
import SchedulesDrawer from "./SchedulesDrawer";
import StatsDrawer from "./StatsDrawer";
import NotificationsDrawer from "./NotificationsDrawer";

// Drawer keys
const DRAWERS = {
  TEAMS: "teams",
  SCHEDULE: "schedule",
  STATS: "stats",
  NOTIFICATIONS: "notifications",
};

const BottomNav = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isCaptain, isPlayer } = useAuth();
  // Use selectedSeason (always populated) not activeSeason (null when status != active/playoffs)
  const { activeSeason, selectedSeason } = useSeason();
  const displaySeason = selectedSeason ?? activeSeason;

  // Pending badge counts (admin only)
  const pendingAdjCount = usePendingAdjustments(!!user && isAdmin);
  const pendingRegCount = usePendingRegistrations(!!user && isAdmin);

  // Active drawer state — null means all closed
  const [activeDrawer, setActiveDrawer] = useState(null);

  // Fetch all drawer data (teams, schedule, stats, legacy admin notifications)
  const {
    upcomingGames,
    myTeam,
    myStats,
    myLastGame,
    leaderboard,
    notifications: legacyNotifications,
    rsvpMap,
    toggleRsvp,
    resolvedTeamId,
  } = useBottomNavData({
    user,
    isAdmin,
    isCaptain,
    isPlayer,
    activeSeason: displaySeason,
  });

  // ── Player / admin notification system ────────────────────────────────────
  // Active for all logged-in users:
  //  - Players/captains: fetches their own player_notifications rows.
  //  - Admins: the player_notifications_admin_read_all RLS policy (added in
  //    migration 20260927001) lets admins read all rows, so we pass `user`
  //    unconditionally and the hook fetches whatever the policy allows.
  const isPlayerOrCaptain = isPlayer || isCaptain;

  const {
    notifications: playerNotifications,
    unreadCount,
    markRead,
    markAllRead,
    deleteNotification,
  } = usePlayerNotifications({
    // Pass user for all roles — admins need DB notifications too.
    user,
    activeSeason: displaySeason,
    isCaptain,
    isAdmin,
    resolvedTeamId,
  });

  // ── Merge notifications ────────────────────────────────────────────────────
  // DB-backed player_notifications (typed) are the primary source for all roles.
  // Legacy admin notifications (ephemeral, role-based) are appended only when
  // the DB-backed system has no admin-type rows yet (graceful fallback).
  const mergedNotifications = React.useMemo(() => {
    // Map every player_notifications row to the shape NotificationsDrawer uses.
    const dbNotifs = playerNotifications.map((n) => ({
      id: n.id,
      type: mapNotifTypeToRole(n.type, isCaptain, isAdmin),
      icon: mapNotifTypeToIcon(n.type),
      title: n.title,
      body: n.body,
      action: n.action_url,
      priority: n.is_read ? 3 : 2,
      is_read: n.is_read,
      ephemeral: n.ephemeral ?? false,
      created_at: n.created_at,
    }));

    // For pure admins (not also a player/captain), also surface legacy
    // ephemeral admin notifications so the badge count stays accurate while
    // the DB-backed admin notifications are being seeded for the first time.
    if (isAdmin && !isPlayerOrCaptain) {
      const legacyAdminNotifs = legacyNotifications.filter(
        (n) => n.type === "admin",
      );
      // Deduplicate: only append legacy ones that have no DB equivalent
      return [...dbNotifs, ...legacyAdminNotifs];
    }

    return dbNotifs;
  }, [
    isPlayerOrCaptain,
    isAdmin,
    isCaptain,
    playerNotifications,
    legacyNotifications,
  ]);

  // Close drawer on route change
  useEffect(() => {
    setActiveDrawer(null);
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleItemPress = useCallback(
    (key) => {
      if (key === "home") {
        setActiveDrawer(null);
        navigate("/");
        return;
      }
      setActiveDrawer((prev) => (prev === key ? null : key));
    },
    [navigate],
  );

  const closeDrawer = useCallback(() => setActiveDrawer(null), []);

  // ── Badge totals ─────────────────────────────────────────────────────────────
  const adminBadge = isAdmin ? pendingAdjCount + pendingRegCount : 0;
  // All roles now use the DB-backed unread count.
  // Pure admins previously fell back to mergedNotifications.length (legacy);
  // now they get unreadCount from usePlayerNotifications like everyone else.
  const notifBadge = unreadCount + (isAdmin ? adminBadge : 0);

  return (
    <>
      {/* ── Bottom Dock ──────────────────────────────────────────────────────── */}
      <BottomDock
        activeDrawer={activeDrawer}
        onItemPress={handleItemPress}
        notifBadge={notifBadge}
        adminBadge={adminBadge}
      />

      {/* ── Teams Drawer ─────────────────────────────────────────────────────── */}
      <TeamsDrawer
        open={activeDrawer === DRAWERS.TEAMS}
        onClose={closeDrawer}
        myTeam={myTeam}
        isCaptain={isCaptain}
        isAdmin={isAdmin}
        user={user}
      />

      {/* ── Schedules Drawer ─────────────────────────────────────────────────── */}
      <SchedulesDrawer
        open={activeDrawer === DRAWERS.SCHEDULE}
        onClose={closeDrawer}
        upcomingGames={upcomingGames}
        teamId={resolvedTeamId}
        rsvpMap={rsvpMap}
        onRsvp={toggleRsvp}
        isAdmin={isAdmin}
        isCaptain={isCaptain}
      />

      {/* ── Stats Drawer ─────────────────────────────────────────────────────── */}
      <StatsDrawer
        open={activeDrawer === DRAWERS.STATS}
        onClose={closeDrawer}
        myStats={myStats}
        myLastGame={myLastGame}
        leaderboard={leaderboard}
        currentUserId={user?.id ?? null}
        userName={user?.name ?? null}
        isAdmin={isAdmin}
        isLoggedIn={!!user}
      />

      {/* ── Notifications Drawer ─────────────────────────────────────────────── */}
      <NotificationsDrawer
        open={activeDrawer === DRAWERS.NOTIFICATIONS}
        onClose={closeDrawer}
        notifications={mergedNotifications}
        isAdmin={isAdmin}
        isCaptain={isCaptain}
        isPlayer={isPlayer}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onDelete={deleteNotification}
      />
    </>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Map a player_notifications.type to the role badge shown in NotificationsDrawer.
 * The returned string must match a key in NotificationsDrawer's RoleBadge config
 * ('admin' | 'captain' | 'player').
 */
function mapNotifTypeToRole(type, isCaptain, isAdmin) {
  switch (type) {
    // ── Admin-facing types (inserted by DB triggers) ──────────────────────────
    case "player_claim_request":
    case "team_registration":
    case "stats_adjustment_request":
      return "admin";
    // ── Captain-facing types ──────────────────────────────────────────────────
    case "stat_adjustment_approved":
      return "captain";
    // ── Player-facing types ───────────────────────────────────────────────────
    case "new_photo_album":
    case "game_score_posted":
    case "claim_approved":
    case "upcoming_game":
      return "player";
    default:
      return isAdmin ? "admin" : "player";
  }
}

/**
 * Map a player_notifications.type to the icon key used in NotificationsDrawer's ICON_MAP.
 */
function mapNotifTypeToIcon(type) {
  switch (type) {
    // ── Admin-facing types ────────────────────────────────────────────────────
    case "player_claim_request":
      return "shield"; // claim / identity verification
    case "team_registration":
      return "users"; // new team / group of people
    case "stats_adjustment_request":
      return "clipboard-list"; // stat correction request
    // ── Captain-facing types ──────────────────────────────────────────────────
    case "stat_adjustment_approved":
      return "clipboard";
    // ── Player-facing types ───────────────────────────────────────────────────
    case "new_photo_album":
      return "camera";
    case "game_score_posted":
      return "basketball";
    case "claim_approved":
      return "shield";
    case "upcoming_game":
      return "calendar";
    default:
      return "zap";
  }
}

export default BottomNav;
